import { create, StateCreator } from 'zustand';

import type { TaskBalancerAccount, TaskBalancerGroup, TaskBalancerToken } from '../types';

type BalancerFormState = {
  // Form data
  settings: TaskBalancerGroup;
  accounts: TaskBalancerAccount[];
  tokens: TaskBalancerToken[];

  // UI state
  isCreating: boolean;
};

type BalancerFormActions = {
  // Individual setters (keep similar API to context)
  setSettings: (settings: TaskBalancerGroup) => void;
  setAccounts: (accounts: TaskBalancerAccount[]) => void;
  setTokens: (tokens: TaskBalancerToken[]) => void;
  setIsCreating: (isCreating: boolean) => void;

  // Utility actions
  resetForm: () => void;
  initializeForm: (data?: {
    settings?: TaskBalancerGroup;
    accounts?: TaskBalancerAccount[];
    tokens?: TaskBalancerToken[];
    isCreating?: boolean;
  }) => void;
};

const DEFAULT_SETTINGS: TaskBalancerGroup = {
  strategy: 'equal',
  check_period: '30s',
  active: true,
};

const balancerFormSlice: StateCreator<BalancerFormState & BalancerFormActions> = set => ({
  // Initial state
  settings: DEFAULT_SETTINGS,
  accounts: [],
  tokens: [],
  isCreating: true,

  // Actions
  setSettings: settings => set({ settings }),
  setAccounts: accounts => set({ accounts }),
  setTokens: tokens => set({ tokens }),
  setIsCreating: isCreating => set({ isCreating }),

  resetForm: () =>
    set({
      settings: DEFAULT_SETTINGS,
      accounts: [],
      tokens: [],
      isCreating: true,
    }),

  initializeForm: data =>
    set({
      settings: data?.settings || DEFAULT_SETTINGS,
      accounts: data?.accounts || [],
      tokens: data?.tokens || [],
      isCreating: data?.isCreating ?? true,
    }),
});

export const useBalancerFormStore = create(balancerFormSlice);
